import { createCopyHook } from '@/core/settings';

/*
Textes de l'espace entreprise (fr / en / es). Chaque texte est d'abord cherché
dans l'API i18n (clé existante `corpo:*`, puis `corpo:v2.<path>`), sinon ici.
*/
const COPY = {
  fr: {
    layout: {
      kicker: 'Espace entreprise',
      tabs: {
        activities: 'Les réservations de votre équipe et leurs documents.',
        team: 'Membres, invitations et rôles.',
        payment: 'Comment votre entreprise règle les réservations.',
        legend: 'Votre abonnement Legend+.',
        params: 'Qui peut rejoindre votre entreprise.',
        profile: 'Raison sociale et contacts de facturation.',
        help: 'Une question ? Notre assistant vous répond.',
      },
    },
    save: {
      unsaved: 'Modifications non enregistrées',
      saving: 'Enregistrement…',
      saved: 'Modifications enregistrées',
      error: 'Une erreur est survenue, réessayez.',
      cancel: 'Annuler',
      save: 'Enregistrer',
    },
    profile: {
      title: "Profil de l'entreprise",
      desc: 'Ces informations apparaissent sur vos devis et vos factures.',
      name: "Nom de l'entreprise",
      address: 'Adresse',
      siret: 'SIRET',
      vat: 'N° de TVA intracommunautaire',
      billing_title: 'Contacts de facturation',
      billing_desc: 'Les devis, factures et relances sont envoyés à ces adresses.',
      email_placeholder: 'prenom@entreprise.fr',
      add: 'Ajouter',
      empty: 'Aucune adresse pour le moment.',
      invalid_email: 'Adresse e-mail invalide.',
      remove: 'Retirer',
    },
    params: {
      title: "Accès à l'espace entreprise",
      desc: 'Qui peut rejoindre votre entreprise et comment les demandes sont validées.',
      domains_title: 'Domaines e-mail autorisés',
      domains_desc: 'Les collaborateurs dont l’adresse utilise un de ces domaines peuvent demander à rejoindre l’entreprise.',
      domain_placeholder: 'entreprise.fr',
      add: 'Ajouter',
      empty: 'Aucun domaine pour le moment.',
      domain_policy: 'Demandes depuis un domaine autorisé',
      other_policy: 'Demandes depuis un autre domaine',
      saved: 'Enregistré',
    },
    team: {
      title: 'Équipe',
      desc: 'Invitez vos collaborateurs et gérez leurs rôles.',
      invite: 'Inviter des membres',
      count: '{count} membre(s)',
      member: 'Membre',
      email: 'E-mail',
      status: 'Statut',
      role: 'Rôle',
      status_active: 'Actif',
      status_pending_invite: 'Invitation envoyée',
      status_pending_email: 'E-mail à confirmer',
      status_need_approval: 'À valider',
      approve: 'Valider',
      resend: 'Renvoyer l’invitation',
      resent: 'Invitation renvoyée',
      remove: 'Retirer',
      confirm_remove: 'Retirer ce membre ?',
      yes: 'Oui',
      no: 'Non',
      you: 'Vous',
      choose_role: 'Choisir un rôle',
      empty_title: 'Aucun membre pour le moment',
      empty_hint: 'Invitez vos collaborateurs pour qu’ils réservent au nom de l’entreprise.',
    },
    payment: {
      title: 'Paiement des réservations',
      desc: 'Choisissez comment votre entreprise règle les réservations de son équipe.',
      sepa: 'Prélèvement SEPA',
      sepa_desc: 'Mandat signé en ligne, prélèvement automatique après chaque réservation.',
      transfer: 'Virement bancaire',
      transfer_desc: 'Chaque facture est réglée par virement selon le délai choisi.',
      saved_title: 'Moyen de paiement enregistré',
      saved_sepa: 'Prélèvement SEPA actif',
      saved_transfer: 'Virement bancaire',
      none: 'Aucun moyen de paiement enregistré.',
      remove: 'Supprimer le moyen de paiement',
      confirm_remove: 'Supprimer ce moyen de paiement ?',
      confirm: 'Confirmer',
      cancel: 'Annuler',
      delay_title: 'Délai de règlement',
      save: 'Enregistrer',
      mandate: 'Voir le mandat',
      change_iban: 'Changer d’IBAN',
    },
    legend: {
      title: 'Legend+',
      desc: 'Votre abonnement Legend+ : offre, prélèvements et moyen de paiement.',
      none_desc: 'Aucun abonnement Legend+ actif. Choisissez une offre pour en profiter.',
      canceled_desc: 'Votre abonnement est résilié. Vous pouvez en souscrire un nouveau.',
    },
    activities: {
      title: 'Activités',
      desc: 'Toutes les réservations de votre équipe, mois par mois, avec les documents à télécharger.',
    },
    help: {
      title: 'Aide',
      desc: 'Posez votre question à notre assistant, il connaît votre compte.',
    },
    messages: {
      login_title: 'Connexion requise',
      email_title: 'Confirmez votre adresse e-mail',
      approval_title: 'Demande en attente',
      admin_title: 'Accès réservé aux administrateurs',
      wrong_title: 'Un instant…',
    },
  },
  en: {
    layout: {
      kicker: 'Company space',
      tabs: {
        activities: 'Your team’s bookings and their documents.',
        team: 'Members, invitations and roles.',
        payment: 'How your company pays for bookings.',
        legend: 'Your Legend+ subscription.',
        params: 'Who can join your company.',
        profile: 'Company details and billing contacts.',
        help: 'A question? Our assistant can help.',
      },
    },
    save: {
      unsaved: 'Unsaved changes',
      saving: 'Saving…',
      saved: 'Changes saved',
      error: 'Something went wrong, please try again.',
      cancel: 'Cancel',
      save: 'Save',
    },
    profile: {
      title: 'Company profile',
      desc: 'Shown on your quotes and invoices.',
      name: 'Company name',
      address: 'Address',
      siret: 'Registration number',
      vat: 'VAT number',
      billing_title: 'Billing contacts',
      billing_desc: 'Quotes, invoices and reminders are sent to these addresses.',
      email_placeholder: 'name@company.com',
      add: 'Add',
      empty: 'No address yet.',
      invalid_email: 'Invalid email address.',
      remove: 'Remove',
    },
    params: {
      title: 'Access to the company space',
      desc: 'Who can join your company and how requests are approved.',
      domains_title: 'Allowed email domains',
      domains_desc: 'People whose email uses one of these domains can ask to join the company.',
      domain_placeholder: 'company.com',
      add: 'Add',
      empty: 'No domain yet.',
      domain_policy: 'Requests from an allowed domain',
      other_policy: 'Requests from another domain',
      saved: 'Saved',
    },
    team: {
      title: 'Team',
      desc: 'Invite your colleagues and manage their roles.',
      invite: 'Invite members',
      count: '{count} member(s)',
      member: 'Member',
      email: 'Email',
      status: 'Status',
      role: 'Role',
      status_active: 'Active',
      status_pending_invite: 'Invitation sent',
      status_pending_email: 'Email to confirm',
      status_need_approval: 'To approve',
      approve: 'Approve',
      resend: 'Resend invitation',
      resent: 'Invitation sent again',
      remove: 'Remove',
      confirm_remove: 'Remove this member?',
      yes: 'Yes',
      no: 'No',
      you: 'You',
      choose_role: 'Choose a role',
      empty_title: 'No members yet',
      empty_hint: 'Invite your colleagues so they can book on behalf of the company.',
    },
    payment: {
      title: 'Booking payments',
      desc: 'Choose how your company pays for its team’s bookings.',
      sepa: 'SEPA direct debit',
      sepa_desc: 'Mandate signed online, automatic debit after each booking.',
      transfer: 'Bank transfer',
      transfer_desc: 'Each invoice is paid by transfer within the chosen term.',
      saved_title: 'Saved payment method',
      saved_sepa: 'SEPA direct debit active',
      saved_transfer: 'Bank transfer',
      none: 'No payment method saved.',
      remove: 'Remove payment method',
      confirm_remove: 'Remove this payment method?',
      confirm: 'Confirm',
      cancel: 'Cancel',
      delay_title: 'Payment term',
      save: 'Save',
      mandate: 'View mandate',
      change_iban: 'Change IBAN',
    },
    legend: {
      title: 'Legend+',
      desc: 'Your Legend+ subscription: plan, charges and payment method.',
      none_desc: 'No active Legend+ subscription. Pick a plan to get started.',
      canceled_desc: 'Your subscription is canceled. You can subscribe again.',
    },
    activities: {
      title: 'Activity',
      desc: 'All your team’s bookings, month by month, with downloadable documents.',
    },
    help: {
      title: 'Help',
      desc: 'Ask our assistant, it knows your account.',
    },
    messages: {
      login_title: 'Sign in required',
      email_title: 'Confirm your email address',
      approval_title: 'Request pending',
      admin_title: 'Admins only',
      wrong_title: 'One moment…',
    },
  },
  es: {
    layout: {
      kicker: 'Espacio empresa',
      tabs: {
        activities: 'Las reservas de tu equipo y sus documentos.',
        team: 'Miembros, invitaciones y roles.',
        payment: 'Cómo paga tu empresa las reservas.',
        legend: 'Tu suscripción Legend+.',
        params: 'Quién puede unirse a tu empresa.',
        profile: 'Datos de la empresa y contactos de facturación.',
        help: '¿Una pregunta? Nuestro asistente te ayuda.',
      },
    },
    save: {
      unsaved: 'Cambios sin guardar',
      saving: 'Guardando…',
      saved: 'Cambios guardados',
      error: 'Se ha producido un error, inténtalo de nuevo.',
      cancel: 'Cancelar',
      save: 'Guardar',
    },
    profile: {
      title: 'Perfil de la empresa',
      desc: 'Aparece en tus presupuestos y facturas.',
      name: 'Nombre de la empresa',
      address: 'Dirección',
      siret: 'CIF / número de registro',
      vat: 'Número de IVA intracomunitario',
      billing_title: 'Contactos de facturación',
      billing_desc: 'Presupuestos, facturas y recordatorios se envían a estas direcciones.',
      email_placeholder: 'nombre@empresa.es',
      add: 'Añadir',
      empty: 'Ninguna dirección todavía.',
      invalid_email: 'Correo electrónico no válido.',
      remove: 'Quitar',
    },
    params: {
      title: 'Acceso al espacio empresa',
      desc: 'Quién puede unirse a tu empresa y cómo se validan las solicitudes.',
      domains_title: 'Dominios de correo autorizados',
      domains_desc: 'Las personas cuyo correo usa uno de estos dominios pueden pedir unirse a la empresa.',
      domain_placeholder: 'empresa.es',
      add: 'Añadir',
      empty: 'Ningún dominio todavía.',
      domain_policy: 'Solicitudes desde un dominio autorizado',
      other_policy: 'Solicitudes desde otro dominio',
      saved: 'Guardado',
    },
    team: {
      title: 'Equipo',
      desc: 'Invita a tus compañeros y gestiona sus roles.',
      invite: 'Invitar miembros',
      count: '{count} miembro(s)',
      member: 'Miembro',
      email: 'Correo',
      status: 'Estado',
      role: 'Rol',
      status_active: 'Activo',
      status_pending_invite: 'Invitación enviada',
      status_pending_email: 'Correo por confirmar',
      status_need_approval: 'Por validar',
      approve: 'Validar',
      resend: 'Reenviar la invitación',
      resent: 'Invitación reenviada',
      remove: 'Quitar',
      confirm_remove: '¿Quitar a este miembro?',
      yes: 'Sí',
      no: 'No',
      you: 'Tú',
      choose_role: 'Elegir un rol',
      empty_title: 'Ningún miembro todavía',
      empty_hint: 'Invita a tus compañeros para que reserven en nombre de la empresa.',
    },
    payment: {
      title: 'Pago de las reservas',
      desc: 'Elige cómo paga tu empresa las reservas de su equipo.',
      sepa: 'Domiciliación SEPA',
      sepa_desc: 'Mandato firmado en línea, cargo automático tras cada reserva.',
      transfer: 'Transferencia bancaria',
      transfer_desc: 'Cada factura se paga por transferencia en el plazo elegido.',
      saved_title: 'Método de pago guardado',
      saved_sepa: 'Domiciliación SEPA activa',
      saved_transfer: 'Transferencia bancaria',
      none: 'Ningún método de pago guardado.',
      remove: 'Eliminar el método de pago',
      confirm_remove: '¿Eliminar este método de pago?',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      delay_title: 'Plazo de pago',
      save: 'Guardar',
      mandate: 'Ver el mandato',
      change_iban: 'Cambiar de IBAN',
    },
    legend: {
      title: 'Legend+',
      desc: 'Tu suscripción Legend+: oferta, cargos y método de pago.',
      none_desc: 'Ninguna suscripción Legend+ activa. Elige una oferta para empezar.',
      canceled_desc: 'Tu suscripción está cancelada. Puedes suscribirte de nuevo.',
    },
    activities: {
      title: 'Actividad',
      desc: 'Todas las reservas de tu equipo, mes a mes, con los documentos descargables.',
    },
    help: {
      title: 'Ayuda',
      desc: 'Pregunta a nuestro asistente, conoce tu cuenta.',
    },
    messages: {
      login_title: 'Inicio de sesión necesario',
      email_title: 'Confirma tu correo electrónico',
      approval_title: 'Solicitud pendiente',
      admin_title: 'Solo administradores',
      wrong_title: 'Un momento…',
    },
  },
};

export const useCopy = createCopyHook(COPY, 'corpo:v2');

export default COPY;
